import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer,
  ReferenceLine, ReferenceArea, ReferenceDot, Text,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import { Tabs, TabsList, TabsTrigger } from '../ui/tabs';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '../ui/tooltip';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../ui/table';
import {
  Zap, AlertCircle, AlertTriangle, CheckCircle2, XCircle, Info, Copy, RotateCcw, Sparkles,
} from 'lucide-react';
import {
  MATERIALS, MATERIAL_BY_KEY, FINISHES, TEMP_LIMITS, PROFILES, DEFAULT_INPUTS, EXAMPLE_INPUTS,
  SPEC_BY_KEY, PREFILL_SPECS, validatePrefill, validateInputs, materialDisplay,
} from '../../lib/busbarTools';
import { busbarToolApi } from '../../lib/toolsApi';
import { useQueryPrefill } from '../../lib/queryPrefill';
import {
  SERIES_COLORS, CHART_GRID, CHART_AXIS, AXIS_TICK_STYLE, AXIS_LABEL_STYLE, LINE_PROPS,
  TOOLTIP_CONTENT_STYLE, TOOLTIP_LABEL_STYLE,
} from './simulator/chartTheme';
import ChartZoomHeader from './simulator/ChartZoomHeader';
import { useChartZoom } from './simulator/useChartZoom';
import ReferenceLineTopLabel, { REFERENCE_LABEL_TOP_MARGIN, referenceLabelAnchorFor } from './simulator/ReferenceLineTopLabel';

const CALC_DEBOUNCE_MS = 400;
const MARK_RED = '#dc2626';
const MARK_DARK = '#334155';

const fmt = (n, d = 1) => (n === null || n === undefined || Number.isNaN(n)
  ? '—'
  : Number(n).toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d }));
const toRaw = (v) => (v === null || v === undefined ? '' : String(v));
const buildRaw = (values) => Object.fromEntries(Object.entries(values).map(([k, v]) => [k, toRaw(v)]));

const CHECK_LABELS = {
  continuous: 'Continuous temperature',
  transient: 'Peak / duty temperature',
  shortCircuit: 'Short-circuit temperature',
  voltageDrop: 'Voltage drop',
};

const FieldLabel = ({ children, unit, tip, htmlFor }) => (
  <div className="flex items-center gap-1.5">
    <Label htmlFor={htmlFor} className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
      {children} {unit && <span className="normal-case text-muted-foreground/70">({unit})</span>}
    </Label>
    {tip && (
      <Tooltip>
        <TooltipTrigger asChild>
          <Info className="h-3.5 w-3.5 text-muted-foreground/70 cursor-help" />
        </TooltipTrigger>
        <TooltipContent className="max-w-xs text-xs">{tip}</TooltipContent>
      </Tooltip>
    )}
  </div>
);

const ErrorText = ({ children, testId }) => (
  <p className="flex items-center gap-1 text-xs text-destructive" data-testid={testId}>
    <AlertCircle className="h-3 w-3 shrink-0" /> {children}
  </p>
);

const Section = ({ title, children, right }) => (
  <div className="space-y-4 p-4 bg-muted/50 rounded-md">
    <div className="flex flex-wrap items-center justify-between gap-2">
      <h3 className="font-chivo text-xs font-bold uppercase tracking-wider text-muted-foreground">{title}</h3>
      {right}
    </div>
    {children}
  </div>
);

const NoteLine = ({ children, tone = 'muted', testId }) => (
  <p
    className={`flex items-start gap-1.5 text-xs leading-relaxed ${tone === 'amber' ? 'text-amber-700' : 'text-muted-foreground'}`}
    data-testid={testId}
  >
    {tone === 'amber' ? <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" /> : <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />}
    <span>{children}</span>
  </p>
);

const BusbarSizingCalculator = () => {
  const prefill = useQueryPrefill(PREFILL_SPECS, validatePrefill);
  const [raw, setRaw] = useState(() => ({ ...buildRaw(DEFAULT_INPUTS), ...prefill.values }));
  const setField = (key, value) => setRaw((prev) => ({ ...prev, [key]: value }));

  const handleMaterial = (value) => {
    // Switching material resets the conductivity to that material's reference value.
    setRaw((prev) => ({ ...prev, material: value, conductivityIacs: String(MATERIAL_BY_KEY[value].iacs) }));
  };
  const handleExample = () => {
    setRaw(buildRaw(EXAMPLE_INPUTS));
    toast.success('Example values loaded.');
  };
  const handleReset = () => setRaw(buildRaw(DEFAULT_INPUTS));

  // ---- Client-side validation (mirrors the backend so it never answers 422) ----
  const { errors: fieldErrors, payload } = useMemo(() => validateInputs(raw), [raw]);
  const hasClientErrors = Object.keys(fieldErrors).length > 0;
  const payloadKey = JSON.stringify(payload);

  const [results, setResults] = useState(null);
  const [serverErrors, setServerErrors] = useState({});
  const [calcError, setCalcError] = useState(null);
  const [loading, setLoading] = useState(false);
  const requestIdRef = useRef(0);

  useEffect(() => {
    if (hasClientErrors) {
      setResults(null);
      setServerErrors({});
      setCalcError(null);
      setLoading(false);
      return undefined;
    }
    const requestId = ++requestIdRef.current;
    setLoading(true);
    const timer = setTimeout(() => {
      busbarToolApi.calculate(JSON.parse(payloadKey))
        .then((outcome) => {
          if (requestId !== requestIdRef.current) return;
          if (outcome.errors) {
            setResults(null);
            setServerErrors(outcome.errors);
            setCalcError(outcome.errors.design || Object.values(outcome.errors)[0]);
          } else {
            setResults(outcome.results);
            setServerErrors({});
            setCalcError(null);
          }
        })
        .catch((e) => {
          if (requestId !== requestIdRef.current) return;
          setResults(null);
          setServerErrors({});
          setCalcError(e.message);
        })
        .finally(() => {
          if (requestId === requestIdRef.current) setLoading(false);
        });
    }, CALC_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [hasClientErrors, payloadKey]);

  const errorFor = (key) => fieldErrors[key] || serverErrors[key];
  const mode = raw.mode;
  const mat = MATERIAL_BY_KEY[raw.material];
  const matDisplay = materialDisplay(raw.material, raw.conductivityIacs);
  const nBars = Number(raw.nBars);

  const handleCopy = async () => {
    if (!results) return;
    const r = results;
    const b = r.bar;
    const lines = [
      'HV Busbar Sizing Calculator',
      `Material: ${r.material.label}, ${fmt(r.material.iacs, 0)}% IACS (ρ20 ${fmt(r.material.rho20NOhmM, 2)} nΩ·m, α ${r.material.alpha20.toFixed(5)}/K)`,
      `Bar: ${r.recommendation ? `recommended ${r.recommendation.size}` : `${fmt(b.width, 1)} × ${fmt(b.thickness, 1)} mm`} × ${r.nBars} per pole, ${fmt(b.areaTotalMm2, 1)} mm² total`,
      `Load: ${raw.currentA} A ${raw.currentType === 'ac' ? `AC ${raw.frequencyHz} Hz` : 'DC'}, ambient ${raw.ambientC} °C, ${r.surface.label} (ε ${fmt(r.surface.emissivity, 2)}), ${raw.environment === 'enclosed' ? 'enclosed' : 'free air'}`,
      `Conductor temperature: ${fmt(b.tempC, 1)} °C (rise ${fmt(b.riseK, 1)} K), limit ${fmt(b.tempLimitC, 0)} °C`,
      `Current at the limit: ${fmt(b.ampacityAtLimitA, 0)} A; current density ${fmt(b.currentDensity, 2)} A/mm²`,
      `R_dc ${fmt(b.rDcPoleUohmPerM, 2)} µΩ/m, Rac/Rdc ${fmt(b.shapeFactor, 3)}, loss ${fmt(b.lossWPerM, 2)} W/m (${fmt(b.lossW, 2)} W), drop ${fmt(b.voltageDropMv, 2)} mV`,
      `Heat shed: convection ${fmt(b.convWPerM, 2)} W/m, radiation ${fmt(b.radWPerM, 2)} W/m; τ ${fmt(b.tauS, 0)} s`,
      ...r.checks.map((c) => `${c.ok ? 'PASS' : 'FAIL'}${c.governing ? ' (governing)' : ''}: ${c.label} ${fmt(c.value, 2)} ${c.unit} vs ${fmt(c.limit, 2)} ${c.unit}`),
      `Short circuit: ${raw.faultCurrentKa} kA for ${raw.clearingTimeMs} ms -> ${fmt(r.shortCircuit.thetaFinalC, 1)} °C, minimum area ${fmt(r.shortCircuit.minAreaMm2, 1)} mm² (k ${fmt(r.shortCircuit.k, 1)})`,
      ...(r.warnings.length ? ['', 'Notes:', ...r.warnings.map((w) => `  - ${w.message}`)] : []),
    ];
    try {
      await navigator.clipboard.writeText(lines.join('\n'));
      toast.success('Results copied to clipboard.');
    } catch {
      toast.error('Could not copy to clipboard.');
    }
  };

  const numField = (key, { tip, label } = {}) => {
    const spec = SPEC_BY_KEY[key];
    const error = errorFor(key);
    const id = `bb-${key}`;
    return (
      <div className="space-y-2 min-w-0" key={key}>
        <FieldLabel htmlFor={id} unit={spec.unit} tip={tip}>{label || spec.label}</FieldLabel>
        <Input
          id={id} type="number" step="any" value={raw[key]}
          onChange={(e) => setField(key, e.target.value)}
          className="font-mono" placeholder={spec.optional ? 'optional' : undefined}
          aria-invalid={!!error} data-testid={`${id}-input`}
        />
        {error && <ErrorText testId={`${id}-error`}>{error}</ErrorText>}
      </div>
    );
  };

  const selectField = (key, options, { label, tip } = {}) => (
    <div className="space-y-2 min-w-0" key={key}>
      <FieldLabel tip={tip}>{label}</FieldLabel>
      <Select value={raw[key]} onValueChange={(v) => setField(key, v)}>
        <SelectTrigger data-testid={`bb-${key}-select`}><SelectValue /></SelectTrigger>
        <SelectContent>
          {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
        </SelectContent>
      </Select>
      {errorFor(key) && <ErrorText testId={`bb-${key}-error`}>{errorFor(key)}</ErrorText>}
    </div>
  );

  const tabsField = (key, options, testPrefix) => (
    <Tabs value={raw[key]} onValueChange={(v) => setField(key, v)}>
      <TabsList className="grid w-full sm:w-80 grid-cols-2">
        {options.map((o) => (
          <TabsTrigger key={o.value} value={o.value} data-testid={`${testPrefix}-${o.value}`}>{o.label}</TabsTrigger>
        ))}
      </TabsList>
    </Tabs>
  );

  return (
    <TooltipProvider>
      <div className="space-y-6">
        <Card className="technical-card">
          <CardHeader>
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div>
                <CardTitle className="font-chivo uppercase tracking-tight flex items-center gap-2">
                  <Zap className="h-5 w-5 text-autoxium-blue" />
                  HV Busbar Sizing Calculator
                </CardTitle>
                <CardDescription>
                  Check a rectangular copper or aluminium busbar, or find the smallest standard size, for
                  continuous, peak and short-circuit current. Results update live as you edit.
                </CardDescription>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Button type="button" variant="outline" size="sm" onClick={handleExample} data-testid="bb-example-btn">
                  <Sparkles className="h-3.5 w-3.5" /> Example
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={handleCopy} disabled={!results} data-testid="bb-copy-btn">
                  <Copy className="h-3.5 w-3.5" /> Copy
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={handleReset} data-testid="bb-reset-btn">
                  <RotateCcw className="h-3.5 w-3.5" /> Reset
                </Button>
              </div>
            </div>
          </CardHeader>
        </Card>

        <div className="grid lg:grid-cols-5 gap-6 items-start">
          <Card className="technical-card lg:col-span-3 min-w-0">
            <CardContent className="pt-6 space-y-6">
              <Tabs value={mode} onValueChange={(v) => setField('mode', v)}>
                <TabsList className="grid grid-cols-2 w-full sm:w-80">
                  <TabsTrigger value="check" data-testid="bb-mode-check">Check a bar</TabsTrigger>
                  <TabsTrigger value="recommend" data-testid="bb-mode-recommend">Recommend a bar</TabsTrigger>
                </TabsList>
              </Tabs>

              <Section title="1. Material">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2 min-w-0">
                    <FieldLabel>Conductor</FieldLabel>
                    <Select value={raw.material} onValueChange={handleMaterial}>
                      <SelectTrigger data-testid="bb-material-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {MATERIALS.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  {numField('conductivityIacs', { tip: 'Electrical conductivity as % of the International Annealed Copper Standard (100% = 58 MS/m at 20 °C). Defaults: Cu-ETP 100%, Al 1350 61% (CDA Pub 22), Al 6101-T61 57% (AFL). Use your supplier\'s certificate value.' })}
                </div>
                {matDisplay && (
                  <p className="font-mono text-xs text-muted-foreground" data-testid="bb-material-props">
                    ρ<sub>20</sub> = <span className="normal-case">{fmt(matDisplay.rho20NOhmM, 2)} nΩ·m</span>
                    {' · '}α<sub>20</sub> = <span className="normal-case">{matDisplay.alpha20.toFixed(5)} /K</span>
                    {mat?.approximation && <Badge variant="outline" className="ml-2 text-[10px] text-amber-700 border-amber-500/40">approximation</Badge>}
                  </p>
                )}
                {mat?.approximation && (
                  <NoteLine tone="amber">6101: α, density and specific heat are borrowed from 1350 (α scaled with conductivity) — approximation.</NoteLine>
                )}
              </Section>

              <Section title="2. Geometry">
                {mode === 'check' ? (
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {numField('widthMm', { tip: 'The larger side of the rectangular section.' })}
                    {numField('thicknessMm', { tip: 'The smaller side. Must not exceed the width.' })}
                    {numField('edgeRadiusMm', { tip: 'Corner radius. The area becomes w·t − (4 − π)·r². 0 = sharp corners.' })}
                  </div>
                ) : (
                  <NoteLine testId="bb-recommend-note">
                    Recommend mode tries every DIN 43671 standard size from 12×2 to 200×10 mm (areas as published,
                    including DIN&apos;s rounded edges) with the bars-per-pole and gap below.
                  </NoteLine>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {numField('lengthM', { tip: 'Length of one pole (one-way). Used for the voltage drop and total loss.' })}
                  {numField('nBars', { tip: 'Parallel bars per pole, stacked face to face. Current is assumed to share equally.' })}
                  {nBars > 1 && numField('spacingMm', { tip: 'Air gap between adjacent bars. CDA Pub 22: parallel bars cool well only when the gap is at least the bar thickness.' })}
                </div>
                <div className="space-y-2">
                  <FieldLabel tip="Edge-on: the wide faces are vertical (best convection). Flat: the wide faces are horizontal. CDA Pub 22 Fig 5.">Orientation</FieldLabel>
                  {tabsField('orientation', [{ value: 'edge', label: 'Edge-on (vertical)' }, { value: 'flat', label: 'Flat (horizontal)' }], 'bb-orientation')}
                </div>
              </Section>

              <Section title="3. Current">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {numField('currentA', { tip: 'Continuous DC or RMS current per pole.' })}
                  <div className="space-y-2 min-w-0">
                    <FieldLabel tip="AC adds the skin-effect factor Rac/Rdc (CDA Pub 22). Inverter phase bars can carry harmonics up to about 1–2 kHz.">Current Type</FieldLabel>
                    {tabsField('currentType', [{ value: 'dc', label: 'DC' }, { value: 'ac', label: 'AC' }], 'bb-current')}
                  </div>
                  {raw.currentType === 'ac' && numField('frequencyHz', { tip: 'Frequency used for the skin effect, 1–2000 Hz.' })}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {selectField('profile', PROFILES, { label: 'Load Profile', tip: 'Single peak: I_peak for t_peak starting from the continuous steady state. Duty cycle: I_peak for t_peak, then the continuous current, repeating every period.' })}
                </div>
                {raw.profile !== 'none' && (
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {numField('peakCurrentA')}
                    {numField('peakDurationS')}
                    {raw.profile === 'duty' && numField('periodS')}
                  </div>
                )}
              </Section>

              <Section title="4. Environment & Limits">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {numField('ambientC', { tip: 'Air temperature around the bar. For an enclosed bar, the air inside the enclosure.' })}
                  {selectField('tempLimit', TEMP_LIMITS, {
                    label: 'Max Conductor Temp',
                    tip: 'IEC 61439-1 Table 6: 70 K for terminals of insulated conductors, 105 K maximum for bare copper, both referred to 35 °C ambient (the sum stays the same at a higher ambient). DIN 43671 rates bars at 65 °C.',
                  })}
                  {raw.tempLimit === 'custom' && numField('maxTempC')}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {selectField('finish', FINISHES.map((f) => ({ value: f.value, label: f.emissivity === null ? f.label : `${f.label} (ε ${f.emissivity.toFixed(2)})` })), {
                    label: 'Surface Finish',
                    tip: 'Sets the surface emissivity ε for radiation. Bright 0.10, partially oxidised 0.30, heavily oxidised 0.70, dull paint 0.90 (CDA Pub 22); tin 0.04 (Engineering Toolbox).',
                  })}
                  {raw.finish === 'custom' && numField('emissivity')}
                </div>
                {raw.finish === 'sleeve' && (
                  <>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {numField('sleeveThicknessMm', { tip: 'Wall thickness of the heat-shrink / sleeve after shrinking.' })}
                      {numField('sleeveConductivity', { tip: 'Thermal conductivity of the sleeve material, from its datasheet.' })}
                    </div>
                    <NoteLine tone="amber">The 0.2 W/m·K default is an approximation — use the sleeve datasheet value.</NoteLine>
                  </>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2 min-w-0">
                    <FieldLabel tip="Enclosed: the bar radiates to walls at the internal air temperature, using the relative emissivity e = ε1ε2/(ε1 + ε2 − ε1ε2) (CDA Pub 22). Free air treats the surroundings as a black body.">Installation</FieldLabel>
                    {tabsField('environment', [{ value: 'free', label: 'Free air' }, { value: 'enclosed', label: 'Enclosed' }], 'bb-env')}
                  </div>
                  {raw.environment === 'enclosed' && numField('wallEmissivity', { tip: 'Emissivity of the enclosure inner wall. 0.9 = painted wall (CDA dull paint value).' })}
                  {numField('airVelocity', { tip: '0 = still air. From 0.5 m/s CDA Pub 22 uses Wa = 120·√v·A·θ.' })}
                </div>
              </Section>

              <Section title="5. Short Circuit">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {numField('faultCurrentKa', { tip: 'Prospective fault current at the feed end of the bar (no bar impedance), as CDA Pub 22 recommends for the worst case.' })}
                  {numField('clearingTimeMs', { tip: 'Time for the fuse / pyro-fuse / breaker to clear the fault. The adiabatic method is valid up to 5 s.' })}
                  {numField('scMaxTempC', { tip: 'Highest conductor temperature allowed at the end of the fault. CDA Pub 22: copper should not exceed ~200 °C.' })}
                </div>
                {mat?.base === 'al' && (
                  <NoteLine tone="amber">For aluminium the 200 °C default is unverified (IEC 60865-1 Table 6 could not be checked) — use your own limit if you have one.</NoteLine>
                )}
              </Section>

              <Section title="6. Voltage (context)">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {numField('systemVoltageV', { tip: 'Optional. Used to express the voltage drop as % of the system voltage.' })}
                  {numField('maxDropPercent', { tip: 'Optional voltage-drop limit, as % of the system voltage, for one pole.' })}
                </div>
                <NoteLine>
                  Creepage and clearance are not calculated. For HV busbars, check insulation coordination
                  (IEC 60664-1) and your vehicle-level HV safety standard separately.
                </NoteLine>
              </Section>
            </CardContent>
          </Card>

          <div className="lg:col-span-2 space-y-4 min-w-0">
            {calcError ? (
              <Card className="technical-card"><CardContent className="pt-6">
                <div className="flex items-start gap-2 text-sm text-destructive" data-testid="bb-calc-error">
                  <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" /> <span>{calcError}</span>
                </div>
              </CardContent></Card>
            ) : hasClientErrors ? (
              <Card className="technical-card"><CardContent className="pt-6">
                <p className="text-sm text-muted-foreground" data-testid="bb-results-placeholder">Fix the highlighted fields to see results.</p>
              </CardContent></Card>
            ) : !results ? (
              <Card className="technical-card"><CardContent className="pt-6">
                <p className="text-sm text-muted-foreground" data-testid="bb-results-loading">Calculating…</p>
              </CardContent></Card>
            ) : (
              <Summary r={results} loading={loading} />
            )}
          </div>
        </div>

        {results && !calcError && !hasClientErrors && (
          <>
            <ChecksTable r={results} />
            {results.recommendation && <RecommendCard r={results} />}
            <TemperatureCurrentChart r={results} />
            {results.transient && results.transient.chart.length > 0 && (
              <TransientChart r={results} peakDurationS={Number(raw.peakDurationS)} />
            )}
            <div className="grid md:grid-cols-2 gap-6">
              <ShortCircuitCard r={results} />
              <DinReferenceCard r={results} />
            </div>
          </>
        )}
      </div>
    </TooltipProvider>
  );
};

// ---------------------------------------------------------------------------
// Summary
// ---------------------------------------------------------------------------

const Stat = ({ label, value, sub, testId, tone }) => (
  <div className="min-w-0">
    <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground mb-1">{label}</p>
    <p className={`font-mono text-xl font-black break-words ${tone || 'text-autoxium-blue'}`} data-testid={testId}>{value}</p>
    {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
  </div>
);

const Row = ({ label, children, testId }) => (
  <div className="flex items-baseline justify-between gap-3 text-xs font-mono py-1 border-b border-structure/60 last:border-0">
    <span className="text-muted-foreground">{label}</span>
    <span className="font-bold text-right" data-testid={testId}>{children}</span>
  </div>
);

const PassBadge = ({ ok, children, testId }) => (
  <Badge
    variant="outline"
    className={`font-mono text-[11px] gap-1 ${ok ? 'text-emerald-700 border-emerald-600/40' : 'text-destructive border-destructive/40'}`}
    data-testid={testId}
  >
    {ok ? <CheckCircle2 className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5" />}
    {children}
  </Badge>
);

const Summary = ({ r, loading }) => {
  const b = r.bar;
  const gov = r.checks.find((c) => c.governing);
  const warnings = r.warnings.filter((w) => w.level === 'warning');
  const infos = r.warnings.filter((w) => w.level !== 'warning');
  return (
    <div className={`space-y-4 transition-opacity ${loading ? 'opacity-60' : ''}`} data-testid="bb-results">
      <Card className={`technical-card ring-1 ${r.ok ? 'ring-emerald-600/30' : 'ring-destructive/40'}`}>
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <CardTitle className="font-chivo uppercase tracking-tight text-base">
              {r.recommendation ? 'Recommended Bar' : 'Bar Check'}
            </CardTitle>
            <PassBadge ok={r.ok} testId="bb-verdict">{r.ok ? 'All limits pass' : 'Fails a limit'}</PassBadge>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {r.recommendation && (
            <Stat
              label="Smallest passing DIN size"
              value={<span className="normal-case">{`${r.recommendation.size} mm${r.nBars > 1 ? ` × ${r.nBars}` : ''}`}</span>}
              sub={<span className="normal-case">{`${fmt(b.areaTotalMm2, 1)} mm² per pole`}</span>}
              testId="bb-recommended-size"
            />
          )}
          <div className="grid grid-cols-2 gap-4">
            <Stat label="Conductor temp" value={`${fmt(b.tempC, 1)} °C`} sub={`rise ${fmt(b.riseK, 1)} K · limit ${fmt(b.tempLimitC, 0)} °C`} testId="bb-temp" tone={b.tempC > b.tempLimitC ? 'text-destructive' : undefined} />
            <Stat label="Current at limit" value={`${fmt(b.ampacityAtLimitA, 0)} A`} sub={`for ${fmt(b.tempLimitC, 0)} °C, same conditions`} testId="bb-ampacity" />
          </div>
          {gov && (
            <p className="text-xs text-muted-foreground" data-testid="bb-governing">
              Governing limit: <span className="font-bold text-foreground">{CHECK_LABELS[gov.key]}</span>
              {' '}({fmt(gov.utilisation * 100, 0)}% used{gov.key === 'voltageDrop' ? '' : ' of the allowed rise'}).
            </p>
          )}
        </CardContent>
      </Card>

      {(warnings.length > 0 || infos.length > 0) && (
        <Card className="technical-card" data-testid="bb-warnings">
          <CardContent className="pt-6 space-y-2">
            {warnings.map((w) => (
              <div key={w.code} className="flex items-start gap-2 rounded-md bg-amber-500/10 border border-amber-500/30 p-2.5 text-xs text-amber-800 leading-relaxed" data-testid={`bb-warning-${w.code}`}>
                <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" /> {w.message}
              </div>
            ))}
            {infos.map((w) => (
              <div key={w.code} className="flex items-start gap-2 rounded-md bg-muted/60 p-2.5 text-xs text-muted-foreground leading-relaxed" data-testid={`bb-warning-${w.code}`}>
                <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" /> {w.message}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <Card className="technical-card">
        <CardHeader><CardTitle className="font-chivo uppercase tracking-tight text-sm">Electrical &amp; Thermal</CardTitle></CardHeader>
        <CardContent>
          <Row label="Cross-section (pole)" testId="bb-area">{fmt(b.areaTotalMm2, 1)} <span className="normal-case">mm²</span></Row>
          <Row label="Current density" testId="bb-density">
            {fmt(b.currentDensity, 2)} <span className="normal-case">A/mm²</span>
          </Row>
          <Row label={`R_dc @ ${fmt(b.tempC, 0)} °C`} testId="bb-rdc">{fmt(b.rDcPoleUohmPerM, 2)} <span className="normal-case">µΩ/m</span></Row>
          <Row label="R_ac / R_dc (skin effect)" testId="bb-shape">
            {fmt(b.shapeFactor, 3)}{b.shapeExtrapolated && <span className="text-amber-700"> (extrapolated)</span>}
          </Row>
          <Row label="Power loss" testId="bb-loss">{fmt(b.lossWPerM, 2)} W/m · {fmt(b.lossW, 2)} W</Row>
          <Row label="Voltage drop (one pole)" testId="bb-drop">
            {fmt(b.voltageDropMv, 2)} mV{b.voltageDropPercent !== null && ` · ${fmt(b.voltageDropPercent, 4)}%`}
          </Row>
          <Row label="Convection / radiation" testId="bb-split">{fmt(b.convWPerM, 2)} / {fmt(b.radWPerM, 2)} W/m</Row>
          {r.surface.finish === 'sleeve' && <Row label="Sleeve surface temp" testId="bb-surface">{fmt(b.surfaceTempC, 1)} °C</Row>}
          <Row label="Thermal time constant τ" testId="bb-tau">{b.tauS === null ? '—' : `${fmt(b.tauS, 0)} s`}</Row>
          {r.transient && (
            <Row label={r.transient.kind === 'duty' ? 'Duty-cycle peak temp' : 'Peak-current temp'} testId="bb-transient">
              {fmt(r.transient.peakTempC, 1)} °C{r.transient.irmsA ? ` (I_rms ${fmt(r.transient.irmsA, 0)} A)` : ''}
            </Row>
          )}
          <Row label="Surface ε / relative e">{fmt(r.surface.emissivity, 2)} / {fmt(r.surface.relativeEmissivity, 3)}</Row>
          <p className="text-[11px] text-muted-foreground pt-2">
            Current density is for information: CDA Pub 22 suggests ≈ <span className="normal-case">{fmt(r.cdaStartCurrentDensity, 0)} A/mm²</span> in still air only as a starting point.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Tables
// ---------------------------------------------------------------------------

const ChartCard = ({ title, description, children, testId }) => (
  <Card className="technical-card min-w-0" data-testid={testId}>
    <CardHeader>
      <CardTitle className="font-chivo uppercase tracking-tight text-sm">{title}</CardTitle>
      {description && <CardDescription>{description}</CardDescription>}
    </CardHeader>
    <CardContent>{children}</CardContent>
  </Card>
);

const valueText = (c) => (c.unit === '%' ? `${fmt(c.value, 3)}%` : `${fmt(c.value, 1)} °C`);
const limitText = (c) => (c.unit === '%' ? `${fmt(c.limit, 2)}%` : `${fmt(c.limit, 0)} °C`);

const ChecksTable = ({ r }) => (
  <ChartCard
    title="Pass / Fail by Limit"
    description="The governing limit (highlighted) is the one using the largest share of its allowance — the temperature rise for thermal limits, the drop for voltage."
    testId="bb-checks"
  >
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Limit</TableHead>
            <TableHead className="text-right">Value</TableHead>
            <TableHead className="text-right hidden sm:table-cell">Limit</TableHead>
            <TableHead className="text-right">Result</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {r.checks.map((c) => (
            <TableRow key={c.key} className={c.governing ? 'bg-autoxium-blue/10 font-semibold' : ''} data-testid={`bb-check-${c.key}`}>
              <TableCell className="text-xs">
                {CHECK_LABELS[c.key]}
                {c.governing && <span className="ml-1.5 font-mono text-[10px] uppercase text-autoxium-blue">governing</span>}
              </TableCell>
              <TableCell className="text-right font-mono text-xs whitespace-nowrap">
                {valueText(c)}
                <span className="block sm:hidden text-[10px] text-muted-foreground">limit {limitText(c)}</span>
              </TableCell>
              <TableCell className="text-right font-mono text-xs whitespace-nowrap hidden sm:table-cell">{limitText(c)}</TableCell>
              <TableCell className="text-right">
                <span className={`inline-flex items-center gap-1 font-mono text-xs font-bold ${c.ok ? 'text-emerald-700' : 'text-destructive'}`}>
                  {c.ok ? <CheckCircle2 className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5" />}
                  <span className="hidden sm:inline">{c.ok ? 'PASS' : 'FAIL'}</span>
                  <span className="sr-only sm:hidden">{c.ok ? 'PASS' : 'FAIL'}</span>
                </span>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  </ChartCard>
);

const MAX_CANDIDATES = 8;

const RecommendCard = ({ r }) => {
  const rec = r.recommendation;
  return (
    <ChartCard
      title="Candidate Standard Sizes"
      description={`DIN 43671 sizes that pass every limit, smallest first (${rec.rejectedSmaller} smaller size${rec.rejectedSmaller === 1 ? '' : 's'} failed). Utilisation is the largest share of any limit's allowance.`}
      testId="bb-recommend"
    >
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Size <span className="normal-case">(mm)</span></TableHead>
                <TableHead className="text-right">Area <span className="normal-case">(mm²)</span></TableHead>
                <TableHead className="text-right">Rise <span className="normal-case">(K)</span></TableHead>
                <TableHead className="text-right">Used</TableHead>
                <TableHead className="hidden sm:table-cell">Governs</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rec.candidates.slice(0, MAX_CANDIDATES).map((c, i) => (
                <TableRow key={c.size} className={i === 0 ? 'bg-autoxium-blue/10 font-semibold' : ''} data-testid={`bb-candidate-${i}`}>
                  <TableCell className="font-mono text-xs">{c.size}{r.nBars > 1 ? ` ×${r.nBars}` : ''}</TableCell>
                  <TableCell className="text-right font-mono text-xs">{fmt(c.areaTotalMm2, 1)}</TableCell>
                  <TableCell className="text-right font-mono text-xs">{fmt(c.riseK, 1)}</TableCell>
                  <TableCell className="text-right font-mono text-xs">{fmt(c.utilisation * 100, 0)}%</TableCell>
                  <TableCell className="hidden sm:table-cell text-xs">{CHECK_LABELS[c.governing]}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <div className="overflow-x-auto" data-testid="bb-per-limit">
          <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground mb-2">Smallest size per limit</p>
          <Table>
            <TableBody>
              {rec.perLimit.map((p) => (
                <TableRow key={p.key}>
                  <TableCell className="text-xs">{CHECK_LABELS[p.key]}</TableCell>
                  <TableCell className="text-right font-mono text-xs">{p.size || 'none'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <p className="text-[11px] text-muted-foreground mt-2">The recommendation is the smallest size that meets all of them at once.</p>
        </div>
      </div>
    </ChartCard>
  );
};

const ShortCircuitCard = ({ r }) => {
  const sc = r.shortCircuit;
  return (
    <ChartCard
      title="Short-Circuit (Adiabatic) Check"
      description="IEC 60949 / IEC 60364-5-54 k-factor method, starting from the continuous operating temperature."
      testId="bb-sc"
    >
      <Row label="I²t">{fmt(sc.i2tA2s / 1e6, 3)} <span className="normal-case">MA²s</span></Row>
      <Row label="K (material)">{fmt(sc.bigK, 1)}</Row>
      <Row label={`k (${fmt(sc.thetaInitialC, 0)} → ${fmt(sc.thetaMaxC, 0)} °C)`}>{fmt(sc.k, 1)}</Row>
      <Row label="Final temperature" testId="bb-sc-final">
        <span className={sc.ok ? 'text-emerald-700' : 'text-destructive'}>{sc.aboveMelting ? '> melting point' : `${fmt(sc.thetaFinalC, 1)} °C`}</span>
      </Row>
      <Row label="Minimum area for this fault" testId="bb-sc-min">
        {sc.minAreaMm2 === null ? '—' : <>{fmt(sc.minAreaMm2, 1)} <span className="normal-case">mm²</span></>}
        {' '}(have {fmt(sc.areaMm2, 1)})
      </Row>
      {!r.material.scLimitSourced && (
        <p className="text-[11px] text-amber-700 pt-2">Aluminium short-circuit limit: unverified default (CDA copper value).</p>
      )}
    </ChartCard>
  );
};

const DinReferenceCard = ({ r }) => {
  const d = r.dinReference;
  return (
    <ChartCard
      title="DIN Cross-Check"
      description="Published DIN rating for this size (35 °C air, 65 °C bar, edge-on, free air, AC ≤ 60 Hz) next to this model at the same conditions."
      testId="bb-din"
    >
      {d ? (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{d.standard} {d.size}{d.nBars > 1 ? ` ×${d.nBars}` : ''}</TableHead>
                <TableHead className="text-right">DIN</TableHead>
                <TableHead className="text-right">Model</TableHead>
                <TableHead className="text-right">Error</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {[['Bare (ε 0.30)', d.bare], ['Painted (ε 0.90)', d.painted]].map(([label, v]) => (
                <TableRow key={label}>
                  <TableCell className="text-xs">{label}</TableCell>
                  <TableCell className="text-right font-mono text-xs">{fmt(v.publishedA, 0)} A</TableCell>
                  <TableCell className="text-right font-mono text-xs">{fmt(v.modelA, 0)} A</TableCell>
                  <TableCell className="text-right font-mono text-xs">{v.errorPercent > 0 ? '+' : ''}{fmt(v.errorPercent, 1)}%</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground" data-testid="bb-din-none">
          No DIN reference: the size is not in the DIN 43671/43670 series, the material is 6101, or there are more than 2 bars per pole.
        </p>
      )}
    </ChartCard>
  );
};

// ---------------------------------------------------------------------------
// Charts (series come from the backend)
// ---------------------------------------------------------------------------

// Left-anchored label for a horizontal reference line. Recharts' <Text> (not a raw
// <text>) so the dev-only visual-edits plugin does not wrap the dynamic child in a <span>.
const HLineLabel = ({ viewBox, text, color, below, right }) => {
  if (!viewBox) return null;
  return (
    <Text
      x={right ? viewBox.x + viewBox.width - 6 : viewBox.x + 6} y={below ? viewBox.y + 4 : viewBox.y - 4}
      verticalAnchor={below ? 'start' : 'end'} textAnchor={right ? 'end' : 'start'}
      fill={color} fontSize={11} fontFamily={AXIS_LABEL_STYLE.fontFamily}
    >
      {text}
    </Text>
  );
};

const niceCeil = (v) => {
  if (!(v > 0)) return 1;
  const step = 10 ** Math.floor(Math.log10(v)) / 2;
  return Math.ceil(v / step) * step;
};

const TemperatureCurrentChart = ({ r }) => {
  const zoom = useChartZoom([0, 'dataMax']);
  const m = r.chartMeta;
  const data = r.chart;
  return (
    <ChartCard
      title="Conductor Temperature vs Current"
      description={`Steady-state temperature of ${r.recommendation ? `the recommended ${r.recommendation.size}` : 'this'} bar at ${fmt(m.ambientC, 0)} °C ambient. The bar reaches the ${fmt(m.tempLimitC, 0)} °C limit at ${fmt(m.ampacityAtLimitA, 0)} A.`}
      testId="bb-chart-temp"
    >
      <ChartZoomHeader isZoomed={zoom.isZoomed} onReset={zoom.resetZoom} testId="zoom-reset-bb-temp" />
      <div className="h-72 md:h-96">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={data} margin={{ top: REFERENCE_LABEL_TOP_MARGIN, right: 16, left: 0, bottom: 24 }}
            onMouseDown={zoom.onMouseDown} onMouseMove={zoom.onMouseMove} onMouseUp={zoom.onMouseUp}
          >
            <CartesianGrid stroke={CHART_GRID} vertical={false} />
            <XAxis
              dataKey="currentA" type="number" domain={zoom.xDomain} allowDataOverflow tick={AXIS_TICK_STYLE}
              tickFormatter={(v) => Math.round(v).toLocaleString()}
              label={{ value: 'Current (A)', position: 'insideBottom', offset: -12, style: AXIS_LABEL_STYLE }}
            />
            <YAxis
              tick={AXIS_TICK_STYLE} width={48}
              domain={[(min) => Math.floor(Math.min(min, m.ambientC) / 10) * 10, (max) => niceCeil(Math.max(max, m.tempLimitC) * 1.03)]}
              label={{ value: 'Temp (°C)', angle: -90, position: 'insideLeft', style: AXIS_LABEL_STYLE }}
            />
            <RechartsTooltip
              contentStyle={TOOLTIP_CONTENT_STYLE} labelStyle={TOOLTIP_LABEL_STYLE}
              formatter={(v) => [`${fmt(v, 1)} °C`, 'Conductor']} labelFormatter={(v) => `${fmt(v, 0)} A`}
            />
            <ReferenceLine y={m.tempLimitC} stroke={MARK_RED} strokeDasharray="6 3"
              label={<HLineLabel text={`Limit ${fmt(m.tempLimitC, 0)} °C`} color={MARK_RED} />} />
            <ReferenceLine x={m.currentA} stroke={MARK_DARK} strokeDasharray="4 4"
              label={<ReferenceLineTopLabel text="I_cont" color={MARK_DARK} anchor={referenceLabelAnchorFor(m.currentA, zoom.xDomain, data, 'currentA')} />} />
            <Line type="monotone" dataKey="tempC" name="Conductor" stroke={SERIES_COLORS[0]} {...LINE_PROPS} />
            <ReferenceDot x={m.currentA} y={m.tempC} r={5} fill={SERIES_COLORS[1]} stroke="#fff" strokeWidth={2} ifOverflow="discard" />
            {zoom.isSelecting && (
              <ReferenceArea x1={zoom.refAreaLeft} x2={zoom.refAreaRight} stroke={CHART_AXIS} fill={CHART_AXIS} fillOpacity={0.15} />
            )}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </ChartCard>
  );
};

const TransientChart = ({ r, peakDurationS }) => {
  const zoom = useChartZoom([0, 'dataMax']);
  const tr = r.transient;
  const data = tr.chart;
  const limit = r.bar.tempLimitC;
  const isPeak = tr.kind === 'peak';
  return (
    <ChartCard
      title={isPeak ? 'Temperature During the Peak' : 'Temperature Over the Duty Cycle'}
      description={isPeak
        ? `Lumped thermal mass from the ${fmt(tr.startTempC, 1)} °C continuous state: peak current, then back to the continuous current. Peak ${fmt(tr.peakTempC, 1)} °C.`
        : `Last cycles after the temperature has settled (started from the RMS-equivalent ${fmt(tr.irmsA, 0)} A steady state). Cycle peak ${fmt(tr.peakTempC, 1)} °C.`}
      testId="bb-chart-transient"
    >
      <ChartZoomHeader isZoomed={zoom.isZoomed} onReset={zoom.resetZoom} testId="zoom-reset-bb-transient" />
      <div className="h-64 md:h-80">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={data} margin={{ top: REFERENCE_LABEL_TOP_MARGIN, right: 16, left: 0, bottom: 24 }}
            onMouseDown={zoom.onMouseDown} onMouseMove={zoom.onMouseMove} onMouseUp={zoom.onMouseUp}
          >
            <CartesianGrid stroke={CHART_GRID} vertical={false} />
            <XAxis
              dataKey="tS" type="number" domain={zoom.xDomain} allowDataOverflow tick={AXIS_TICK_STYLE}
              tickFormatter={(v) => (v >= 100 ? Math.round(v).toLocaleString() : Number(v).toFixed(v >= 10 ? 0 : 1))}
              label={{ value: 'Time (s)', position: 'insideBottom', offset: -12, style: AXIS_LABEL_STYLE }}
            />
            <YAxis
              tick={AXIS_TICK_STYLE} width={48}
              domain={[(min) => Math.floor(min / 5) * 5, (max) => niceCeil(Math.max(max, limit) * 1.03)]}
              label={{ value: 'Temp (°C)', angle: -90, position: 'insideLeft', style: AXIS_LABEL_STYLE }}
            />
            <RechartsTooltip
              contentStyle={TOOLTIP_CONTENT_STYLE} labelStyle={TOOLTIP_LABEL_STYLE}
              formatter={(v) => [`${fmt(v, 1)} °C`, 'Conductor']} labelFormatter={(v) => `${fmt(v, 1)} s`}
            />
            <ReferenceLine y={limit} stroke={MARK_RED} strokeDasharray="6 3"
              label={<HLineLabel text={`Limit ${fmt(limit, 0)} °C`} color={MARK_RED} right />} />
            {isPeak && (
              <ReferenceLine x={peakDurationS} stroke={MARK_DARK} strokeDasharray="4 4"
                label={<ReferenceLineTopLabel text="end of peak" color={MARK_DARK} anchor={referenceLabelAnchorFor(peakDurationS, zoom.xDomain, data, 'tS')} />} />
            )}
            <Line type="monotone" dataKey="tempC" name="Conductor" stroke={SERIES_COLORS[1]} {...LINE_PROPS} />
            {zoom.isSelecting && (
              <ReferenceArea x1={zoom.refAreaLeft} x2={zoom.refAreaRight} stroke={CHART_AXIS} fill={CHART_AXIS} fillOpacity={0.15} />
            )}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </ChartCard>
  );
};

export default BusbarSizingCalculator;
